import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Loader2Icon, MinusCircleIcon, PlusIcon } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  AddLanguageSkillsDocument,
  LanguageEnum,
  LanguageProficiencyLevel,
  LanguageSkill,
  PublicLanguageSkill,
} from '@sandtech-hub-monorepo/ehub-client';

interface ILanguageSelectionItem {
  id: string;
  language?: LanguageEnum | null;
  level?: LanguageProficiencyLevel | null;
}

export const LanguagesSectionEditDialog = ({
  open,
  defaultValues,
  maxLanguagesCount = 10,
  handleOpenChange,
}: {
  open: boolean;
  defaultValues: LanguageSkill[] | PublicLanguageSkill[];
  maxLanguagesCount?: number;
  handleOpenChange: (open: boolean) => void;
}) => {
  const [fields, setFields] = useState<ILanguageSelectionItem[]>([]);
  const [validationTriggered, setValidationTriggered] = useState(false);

  const [addLanguageSkills, { loading }] = useMutation(
    AddLanguageSkillsDocument
  );

  useEffect(() => {
    setFields(
      defaultValues.map((defLang, index) => ({
        id: defLang.id,
        language: Object.keys(LanguageEnum)[
          Object.values(LanguageEnum).indexOf(defLang.language)
        ] as LanguageEnum,
        level: Object.keys(LanguageProficiencyLevel)[
          Object.values(LanguageProficiencyLevel).indexOf(
            defLang.proficiencyLevel
          )
        ] as LanguageProficiencyLevel,
      }))
    );
  }, [open, defaultValues]);

  const handleAddField = () => {
    if (fields.every((f) => f.language != null && f.level != null)) {
      setFields([
        ...fields,
        { id: '-' + fields.length, language: null, level: null },
      ]);
    } else {
      setValidationTriggered(true);
    }
  };

  const handleRemoveField = (removedField: ILanguageSelectionItem) => {
    const filteredArray: ILanguageSelectionItem[] = [];

    fields.forEach((f) => {
      if (f.id !== removedField.id) {
        filteredArray.push({
          ...f,
        });
      }
    });

    setFields(filteredArray);
  };

  useEffect(() => {
    setValidationTriggered(false);
  }, [open]);

  const onSelectChange = (
    key: string,
    value: string,
    modifiedField: ILanguageSelectionItem
  ) => {
    const index = fields.findIndex((f) => f.id === modifiedField.id);
    if (index !== -1) {
      setFields([
        ...fields.slice(0, index),
        { ...modifiedField, [key]: value },
        ...fields.slice(index + 1),
      ]);
    }
  };

  const handleSave = async () => {
    setValidationTriggered(true);

    if (!fields.every((f) => f.language != null && f.level != null)) {
      return;
    }

    const inputLanguages = fields
      .filter((f) => f.language && f.level)
      .map((f) => ({
        language:
          LanguageEnum[f.language as unknown as keyof typeof LanguageEnum],
        proficiencyLevel:
          LanguageProficiencyLevel[
            f.level as unknown as keyof typeof LanguageProficiencyLevel
          ],
      }));

    await addLanguageSkills({
      variables: {
        input: {
          languages: inputLanguages,
        },
      },
    });

    handleOpenChange(false);
  };

  const availableLanguagesList = Object.keys(LanguageEnum).filter((lng) => {
    return !fields.find((f) => f.language === lng);
  });

  const dirty = useMemo(
    () =>
      defaultValues.map(({ id }) => id).join('_') !==
      fields.map(({ id }) => id).join('_'),
    [fields, defaultValues]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      className={`bg-card`}
      title="Languages"
    >
      <div className="flex flex-col gap-2 w-full overflow-hidden">
        {fields.map((field) => (
          <div key={field.id} className="flex gap-2 items-center">
            <Select
              onValueChange={(val) => onSelectChange('language', val, field)}
              defaultValue={fields
                .find(({ id }) => id === field.id)
                ?.language?.toString()}
            >
              <div className="flex-[1] overflow-hidden">
                <SelectTrigger
                  className={`${
                    field.language === null &&
                    validationTriggered &&
                    'text-destructive border-destructive'
                  }`}
                >
                  <span className="overflow-hidden !block whitespace-nowrap text-ellipsis">
                    <SelectValue placeholder="Select a language">
                      {field.language}
                    </SelectValue>
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {availableLanguagesList.map((lng) => (
                    <SelectItem key={lng} value={lng}>
                      {lng}
                    </SelectItem>
                  ))}
                </SelectContent>
              </div>
            </Select>

            <Select
              onValueChange={(val) => onSelectChange('level', val, field)}
              defaultValue={fields
                .find(({ id }) => id === field.id)
                ?.level?.toString()}
            >
              <div className="flex-[1] overflow-hidden">
                <SelectTrigger
                  className={`flex-[1] overflow-hidden ${
                    field.level === null &&
                    validationTriggered &&
                    'text-destructive border-destructive'
                  }`}
                >
                  <span className="overflow-hidden !block whitespace-nowrap text-ellipsis">
                    <SelectValue placeholder="Choose level" />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(LanguageProficiencyLevel).map((lvl) => (
                    <SelectItem key={lvl} value={lvl}>
                      {lvl}
                    </SelectItem>
                  ))}
                </SelectContent>
              </div>
            </Select>

            <MinusCircleIcon
              className="h-5 cursor-pointer"
              onClick={() => {
                handleRemoveField(field);
              }}
              size={40}
            />
          </div>
        ))}

        {fields.length < maxLanguagesCount && (
          <div className="flex justify-end">
            <Button
              className="flex gap-1"
              variant="link"
              onClick={handleAddField}
            >
              <PlusIcon size={16} /> Add Language
            </Button>
          </div>
        )}
      </div>

      <DialogFooter className="flex flex-col justify-end sm:flex-row gap-1">
        <Button variant="outline" onClick={() => handleOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!dirty || loading}>
          {loading ? <Loader2Icon className="animate-spin" /> : 'Save'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
